/**
 * Central "Total Sanctioned" ADD-ON rule — Feb 2026, revised Sep 10, 2026 (v3).
 *
 * The Sanctioned card on Executive / Trustee Overview shows the DB base amount
 * PLUS an "add-on" that captures grants still in the pipeline. The add-on is
 * FY-dependent because Finance approved two variants of the Financial-page tables:
 *
 *  ┌──────────────────────────────────┬────────────┬────────────────────────────────┬────────────┐
 *  │ FY variant                       │ Grants in  │ Strategic Initiatives (Balance │  Add-on    │
 *  │                                  │ Progress   │ Commitment)                    │            │
 *  ├──────────────────────────────────┼────────────┼────────────────────────────────┼────────────┤
 *  │ FY 25-26  (also FY 24-25 default)│ 19.62 Cr   │ 1,051.53 Cr (no drawdown)      │ 1,071.15 Cr│
 *  │ FY 26-27  / multi-FY / All Years │ 11.34 Cr   │   925.22 Cr (drawdown 126.31)  │   936.56 Cr│
 *  └──────────────────────────────────┴────────────┴────────────────────────────────┴────────────┘
 *
 * Guardrails (add-on suppressed):
 *   • sole selected FY = FY 2024-25 → suppressed (base value only, no add-on)
 *   • view_mode = "snapshot" (New)  → suppressed
 *   • status    = "Closed"          → suppressed
 *   • any Theme / Trust / Grantee filter set → suppressed
 *   • any state/district drill-down set → suppressed
 *
 * Under multi-FY the add-on is applied ONCE (not multiplied by FY count).
 */

// ── FY 25-26 variant ────────────────────────────────────────────────────────
export const UPLIFT_FY2526_CR = 1071.15;                    // 19.62 + 1,051.53
export const UPLIFT_FY2526_GRANTS_IN_PROGRESS_CR = 19.62;
export const UPLIFT_FY2526_BULK_BALANCE_CR = 1051.53;

// ── FY 26-27 variant (also used for multi-FY / All Years) ──────────────────
export const UPLIFT_FY2627_CR = 936.56;                     // 11.34 + 925.22
export const UPLIFT_FY2627_GRANTS_IN_PROGRESS_CR = 11.34;
export const UPLIFT_FY2627_BULK_BALANCE_CR = 925.22;
export const STRATEGIC_INITIATIVES_DRAWDOWN_CR = 126.31;    // shown in FY 26-27 table

// Back-compat exports (older code refs — kept until fully migrated).
export const SANCTIONED_UPLIFT_CR = UPLIFT_FY2526_CR;
export const SANCTIONED_UPLIFT_GRANTS_IN_PROGRESS_CR = UPLIFT_FY2526_GRANTS_IN_PROGRESS_CR;
export const SANCTIONED_UPLIFT_BULK_BALANCE_CR = UPLIFT_FY2526_BULK_BALANCE_CR;

/**
 * Pick the FY variant to render on the Financial page tables and to add on top
 * of Sanctioned on Executive/Trustee cards.
 *
 * Rule (per Finance direction Sep 10, 2026):
 *   • Sole FY = 2024-25   → FY 25-26 variant (both tables mirror FY 25-26)
 *   • Sole FY = 2025-26   → FY 25-26 variant
 *   • Sole FY = 2026-27   → FY 26-27 variant
 *   • Multi-FY / empty    → FY 26-27 variant
 */
export function pickUpliftVariant(filters) {
  const fyArr = Array.isArray(filters?.fy) ? filters.fy : (filters?.fy ? [filters.fy] : []);
  const isSoloFy2526 = fyArr.length === 1 && fyArr[0] === "FY 2025-26";
  const isSoloFy2425 = fyArr.length === 1 && fyArr[0] === "FY 2024-25";
  if (isSoloFy2526 || isSoloFy2425) {
    return {
      key: "fy2526",
      total: UPLIFT_FY2526_CR,
      grantsInProgress: UPLIFT_FY2526_GRANTS_IN_PROGRESS_CR,
      strategicInitiatives: UPLIFT_FY2526_BULK_BALANCE_CR,
      showDrawdown: false,
    };
  }
  return {
    key: "fy2627",
    total: UPLIFT_FY2627_CR,
    grantsInProgress: UPLIFT_FY2627_GRANTS_IN_PROGRESS_CR,
    strategicInitiatives: UPLIFT_FY2627_BULK_BALANCE_CR,
    showDrawdown: true,
  };
}

export function shouldApplyUplift(filters, viewMode, selectedState, selectedDistrict) {
  const fyArr = Array.isArray(filters?.fy) ? filters.fy : (filters?.fy ? [filters.fy] : []);
  // FY rule: excluded only when the SOLE selected FY is FY 2024-25.
  const onlyFy2425 = fyArr.length === 1 && fyArr[0] === "FY 2024-25";
  if (onlyFy2425) return false;
  // Portfolio-wide guardrails.
  if (viewMode === "snapshot") return false;
  if (filters?.status === "Closed") return false;
  if (filters?.theme || filters?.trust || filters?.grantee) return false;
  if (selectedState || selectedDistrict) return false;
  return true;
}

/**
 * Apply the FY-appropriate add-on to a live-computed Sanctioned amount.
 * Returns the base unchanged when guardrails suppress the add-on.
 */
export function applyUplift(baseCr, filters, viewMode, selectedState, selectedDistrict) {
  const base = Number(baseCr || 0);
  if (!isFinite(base)) return 0;
  if (!shouldApplyUplift(filters, viewMode, selectedState, selectedDistrict)) return base;
  return base + pickUpliftVariant(filters).total;
}
